<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed z-10 inset-0 overflow-y-auto" @close="closeModal">
      <div
        class="flex items-end justify-center min-h-screen px-4 pb-20 text-center sm:block sm:p-0"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <DialogOverlay class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <!-- This element is to trick the browser into centering the modal contents. -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enter-to="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leave-from="opacity-100 translate-y-0 sm:scale-100"
          leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        >
          <div
            class="
              inline-block
              align-bottom
              bg-white
              rounded-3xl
              px-8
              py-4
              2xl:py-8
              md:absolute
              md:top-1/2
              md:left-1/2
              transform
              md:-translate-x-1/2 md:-translate-y-1/2
              text-left
              overflow-hidden
              shadow-xl
              transition-all
              w-full
              sm:max-w-4xl
              sm:w-full
            "
          >
            <div>
              <div>
                <div class="border-b pb-2 border-gray-200">
                  <div class="inline-flex w-full justify-between">
                    <h2 class="mb-1 2xl:mb-4 text-xl leading-7 font-bold text-vice-teal-900">
                      Before we start...
                    </h2>
                    <CrossGrayIcon class="h-7 w-7 cursor-pointer" @click="closeModal" />
                  </div>
                  <p class="text-md font-medium text-vice-teal-900 mt-1">
                    The remote consultation you are about to join is a live interaction with a real
                    patient.
                    <br />
                    Please ensure that you meet the necessary
                    <strong class="font-bold">platform requirements</strong>
                    and
                    <strong class="font-bold">adhere to the professional standards</strong>
                    expected in all clinical encounters. expected in all clinician encounters.
                  </p>
                </div>
                <div class="flex mt-2 2xl:mt-4 border-b pb-2 border-gray-200">
                  <p class="mb-0 2xl:mb-4 text-lg leading-10 font-bold text-vice-teal-900">
                    <span
                      class="
                        inline-block
                        h-7
                        w-7
                        mr-5
                        rounded-full
                        text-sm text-center
                        leading-7
                        bg-vice-teal-400
                        text-white
                      "
                    >
                      1
                    </span>
                    Check platform requirements
                  </p>
                </div>
                <div class="flex mt-2">
                  <p class="text-lg leading-10 font-bold text-vice-teal-900">
                    <span
                      class="
                        inline-block
                        h-7
                        w-7
                        mr-5
                        rounded-full
                        text-sm text-center
                        leading-7
                        text-white
                      "
                      :class="checkbox ? 'bg-vice-teal-400' : 'bg-gray-500'"
                    >
                      2
                    </span>
                    Review professional standards
                  </p>
                </div>
                <div class="flex flex-col md:flex-row gap-1 w-full">
                  <ul
                    role="list"
                    class="
                      divide-y divide-gray-300
                      bg-vice-light-grey
                      py-7
                      px-6
                      rounded-l-lg
                      my-2
                      2xl:my-5
                      w-full
                      md:w-1/2
                    "
                  >
                    <li class="pb-5 text-md font-bold text-vice-dark-teal">
                      These responsibilities include:
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/responsibilities-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Professional and ethical responsibilities
                      </p>
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/scale-of-justice-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Legal responsibilities
                      </p>
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/quality-assurance-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Patient safety and quality improvement
                      </p>
                    </li>
                  </ul>

                  <ul
                    role="list"
                    class="
                      step-student-session-info
                      divide-y divide-gray-300
                      bg-vice-light-grey
                      py-7
                      px-6
                      w-full
                      md:w-1/2
                      rounded-r-lg
                      my-2
                      2xl:my-5
                    "
                  >
                    <li class="pb-5 text-md font-bold text-vice-dark-teal">
                      You will be asked to:
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/uncertainty-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Deal with complexity and uncertainty
                      </p>
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/safeguard-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Safeguard vulnerable patients
                      </p>
                    </li>
                    <li class="py-4 flex items-center">
                      <img class="h-12 w-16" src="/image/icons/team-icon.svg" />
                      <p class="ml-10 text-md font-medium text-vice-dark-grey">
                        Show leadership and team working
                      </p>
                    </li>
                  </ul>
                </div>
              </div>
              <div class="flex justify-center gap-2 items-center">
                <input
                  data-cy="agree-terms-session-entry"
                  id="agree-checkbox"
                  type="checkbox"
                  @click="checkbox = !checkbox"
                  :class="[{ 'checkbox-highlighted': checkBoxHighlighted }]"
                  class="
                    step-student-confirm
                    focus:ring-vice-teal-500
                    ring-vice-red
                    cursor-pointer
                    h-4
                    w-4
                    text-vice-teal-600
                    border-gray-300
                    rounded
                  "
                />
                <label for="agree-checkbox" class="text-md text-vice-dark-grey font-bold">
                  I have read the
                  <a :href="route('dashboard', { tab: 'terms-of-use' })" target="_blank">
                    <span class="text-vice-teal-400 underline">Terms of Use</span>
                  </a>
                  and am aware of my responsibilities and will act upon them.
                </label>
              </div>
              <div class="mt-2 2xl:mt-6 flex justify-around">
                <ViceButton
                  data-cy="entry-start-session"
                  @click="enterSession"
                  class="cursor-pointer step-confirm-start-session"
                  :class="[
                    { 'opacity-60': !checkbox },
                    { 'sm:bg-gray-150 sm:text-black': !checkbox },
                    { 'cursor-not-allowed opacity-60': alreadyPresent },
                  ]"
                  icon="LogoutIcon"
                  :loading="false"
                  red
                >
                  Enter session
                </ViceButton>
              </div>
              <div
                v-if="alreadyPresent"
                class="
                  flex
                  gap-2
                  justify-center
                  items-center
                  mt-5
                  p-5
                  rounded-xl
                  border-2
                  bg-vice-rose-400
                  transition-all
                "
                :class="[{ ' highlighted ': highlighted }]"
              >
                <p class="text-xs font-medium">
                  You already have a VCE session open in another tab or window. Please close the
                  existing session before starting a new one.
                </p>
              </div>
            </div>
          </div>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { ref, watch } from 'vue';
import { usePage } from '@inertiajs/inertia-vue3';
import { DialogOverlay, Dialog, TransitionChild, TransitionRoot } from '@headlessui/vue';
import useTourManager from '@/Composables/useTourManager';
import { userEntryModalTourSteps } from '@/Data/tourSteps';
import SessionAnalyticsService from '@/Services/SessionAnalyticsService';
import ViceButton from '@/Components/Buttons/ViceButton.vue';
import CrossGrayIcon from '@/Components/Icons/CrossGrayIcon.vue';

const userEntryModalTour = useTourManager('userEntryModalTour', userEntryModalTourSteps);

export default {
  name: 'ParticipantChecklist',
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    CrossGrayIcon,
    ViceButton,
    Dialog,
    DialogOverlay,
    TransitionChild,
    TransitionRoot,
  },
  emits: ['closeSessionEntryWizard'],
  setup(props, { emit }) {
    const pageProps = usePage().props.value;
    const { session, user } = pageProps;
    const checkbox = ref(false);
    const highlighted = ref(false);
    const alreadyPresent = ref(false);
    const presenceInterval = ref(null);
    const checkBoxHighlighted = ref(false);

    const closeModal = () => {
      if (presenceInterval.value) {
        clearInterval(presenceInterval.value);
        presenceInterval.value = null;
      }

      checkbox.value = false;
      userEntryModalTour.cancelTour();
      emit('closeSessionEntryWizard');
    };

    const learningGoalsAreSet = !!pageProps?.session?.reflection?.my_goals;
    const steps = ref([
      { id: '01', name: 'Accept participation', description: '', status: 'complete' },
      {
        id: '02',
        name: 'Set learning goals',
        description: '',
        status: learningGoalsAreSet ? 'complete' : 'current',
      },
      {
        id: '03',
        name: 'Enter session',
        description: '',
        status: learningGoalsAreSet ? 'current' : 'upcoming',
      },
    ]);

    const learningGoalsAdded = () => {
      goToStep(2);
    };

    const checkUserPresenceTo = async () => {
      try {
        alreadyPresent.value = await SessionAnalyticsService.isUserAlreadyPresent(
          session.unique_id,
          user.unique_id,
        );
      } catch (error) {
        console.error('Error checking user presence:', error);
      }
    };

    watch(
      () => props.open,
      (newVal) => {
        if (newVal) {
          presenceInterval.value = setInterval(() => {
            checkUserPresenceTo();
          }, 1000);

          setTimeout(() => {
            userEntryModalTour.startTour();
          }, 500);
        }
      },
    );

    const goToStep = (stepIndex) => {
      if (stepIndex == 0) return;

      steps.value.forEach((step, index) => {
        // eslint-disable-next-line no-nested-ternary
        step.status = index < stepIndex ? 'complete' : index > stepIndex ? 'upcoming' : 'current';
      });
    };

    const highlightCheckbox = () => {
      checkBoxHighlighted.value = true;
      setTimeout(() => {
        checkBoxHighlighted.value = false;
      }, 600);
    };

    const enterSession = () => {
      if (!checkbox.value) {
        highlightCheckbox();
      }

      if (checkbox.value && !alreadyPresent.value) {
        closeModal();
        localStorage.setItem('_vice:userEntryModalTour_current_step', 3);
        window.location.href = route('session.show', session.unique_id);
      }

      if (alreadyPresent.value) {
        highlighted.value = true;
        setTimeout(() => {
          highlighted.value = false;
        }, 600);
      }
    };

    return {
      steps,
      session,
      checkbox,
      pageProps,
      highlighted,
      alreadyPresent,
      presenceInterval,
      checkBoxHighlighted,
      goToStep,
      closeModal,
      enterSession,
      highlightCheckbox,
      learningGoalsAdded,
    };
  },
};
</script>

<style scoped>
.highlighted {
  background-color: rgb(255 84 109);
  border: 2px solid #bd0517;
  box-shadow: 0 0 10px rgb(69, 0, 14);
  transition: all 0.3s ease;
}

input[type='checkbox'] {
  appearance: none;
  width: 1rem;
  height: 1rem;
  border: 2px solid red;
  border-radius: 0.25rem;
  cursor: pointer;
}

input[type='checkbox']:checked {
  border: 2px solid #0d9488;
}

input[type='checkbox']:focus {
  outline: none !important;
  box-shadow: none !important;
}

.checkbox-highlighted {
  --tw-shadow: 0px 0px 3px 1px rgb(255 0 0);
  box-shadow: 0 0 rgb(255 0 0), var(--tw-shadow);
  transition: box-shadow 0.3s ease;
}
</style>
